import React, { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import { IMaskInput } from "react-imask";
import {
  TextInputContainer,
  TextInputField,
  Label,
} from "../TextInput/TextInput.styled";

interface PhoneInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  required?: boolean;
  helperText?: string;
  error?: boolean;
}

// Custom IMask adapter for MUI InputBase
// This is the standard MUI pattern for third-party formatted inputs
// see: https://mui.com/material-ui/react-text-field/#integration-with-3rd-party-input-libraries
interface PhoneMaskCustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const PhoneMaskCustom = forwardRef<HTMLInputElement, PhoneMaskCustomProps>(
  function PhoneMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="+{1} (000) 000-0000"
        inputRef={ref}
        onAccept={(value: string) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  },
);

const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  label,
  placeholder,
  value = "",
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  fullWidth = true,
  width,
  required = false,
  helperText,
  error = false,
}) => {
  return (
    <Box sx={{ width: fullWidth ? "100%" : width }}>
      {label && (
        <Label>
          {label}
          {required && "*"}
        </Label>
      )}
      <TextInputContainer sx={{ height: "48px" }} error={error || undefined}>
        <TextInputField
          value={value}
          onChange={onChange as React.ComponentProps<typeof TextInputField>["onChange"]}
          name={name}
          placeholder={placeholder || "+1 (___) ___-____"}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          inputComponent={PhoneMaskCustom as any}
          disabled={disabled}
          onBlur={onBlur as React.ComponentProps<typeof TextInputField>["onBlur"]}
          onFocus={onFocus as React.ComponentProps<typeof TextInputField>["onFocus"]}
          fullWidth
        />
      </TextInputContainer>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? "status.error" : "text.secondary",
            fontSize: "12px",
            mt: 0.5,
            display: "block",
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default PhoneInput;
